import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { ApexDatePicker, toIsoDate } from "../ApexDatePicker";
import { Loader2, Trash2, RefreshCw } from "lucide-react";

/**
 * Admin-only panels that share the same shape (data-driven list + form):
 *  - UsersPanel     → list users, change role, reset password, delete
 *  - PromoPanel     → create / list / delete promo codes
 * Each one is self-contained and reachable only from the admin Dashboard tabs.
 * (The old exact-time-slot CalendarPanel was replaced by AdminCalendarOverview
 * — Round 37's half-day preferred-days system.)
 */

export function UsersPanel() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    api.get("/admin/users/all").then(r => setUsers(r.data || [])).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const changeRole = async (u, role) => {
    if (!confirm(`Změnit roli na ${role}?`)) return;
    await api.put(`/admin/users/${u.id}/role?new_role=${role}`);
    load();
  };
  const resetPwd = async (u) => {
    const pwd = prompt(`Nové heslo pro ${u.email}:`);
    if (!pwd) return;
    await api.post(`/admin/users/${u.id}/reset-password?new_password=${encodeURIComponent(pwd)}`);
    alert("Heslo změněno a odesláno emailem.");
  };
  const del = async (u) => {
    if (!confirm(`Smazat ${u.email}? Tato akce je nevratná.`)) return;
    await api.delete(`/admin/users/${u.id}/delete`);
    load();
  };

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-apex-gold" />;

  return (
    <div>
      <h3 className="font-display text-2xl text-apex-cream font-bold mb-4">Uživatelé ({users.length})</h3>
      <div className="overflow-x-auto rounded-2xl border border-apex-line">
        <table className="w-full text-sm">
          <thead className="text-left text-apex-cream/60 text-xs uppercase tracking-wider">
            <tr className="border-b border-apex-line">
              <th className="px-4 py-3">Jméno</th><th>Email</th><th>Telefon</th><th>Role</th><th className="text-right pr-4">Akce</th>
            </tr>
          </thead>
          <tbody className="text-apex-cream">
            {users.map(u => (
              <tr key={u.id} className="border-b border-apex-line/50 hover:bg-apex-card/50">
                <td className="px-4 py-3">{u.firstName} {u.lastName}</td>
                <td>{u.email}</td>
                <td>{u.phone || "—"}</td>
                <td>
                  <select defaultValue={u.role} onChange={(e) => changeRole(u, e.target.value)} className="bg-apex-bg border border-apex-line rounded-md px-2 py-1 text-xs">
                    <option value="customer">customer</option>
                    <option value="employee">employee</option>
                    <option value="admin">admin</option>
                  </select>
                </td>
                <td className="text-right pr-4">
                  <button onClick={() => resetPwd(u)} className="text-xs text-apex-gold hover:underline mr-3"><RefreshCw className="w-3 h-3 inline" /> Heslo</button>
                  <button onClick={() => del(u)} className="text-xs text-red-300 hover:underline"><Trash2 className="w-3 h-3 inline" /> Smazat</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function PromoPanel() {
  const [list, setList] = useState([]);
  const [code, setCode] = useState("");
  const [discount, setDiscount] = useState(5);
  const [type, setType] = useState("multiple");
  const [expiresAt, setExpiresAt] = useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() + 3);
    return d.toISOString().slice(0, 10);
  });
  const [maxUses, setMaxUses] = useState(10);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/promo/list").then(r => setList(r.data || []));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/promo/create", {
        code: code.toUpperCase(),
        discount: Number(discount),
        type,
        expiresAt: new Date(expiresAt).toISOString(),
        maxUses: type === "multiple" ? Number(maxUses) : null,
      });
      setCode("");
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Chyba");
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!confirm("Smazat promo kód?")) return;
    await api.delete(`/promo/${id}`);
    load();
  };

  return (
    <div>
      <h3 className="font-display text-2xl text-apex-cream font-bold mb-4">Promo kódy</h3>
      <form onSubmit={create} className="rounded-2xl bg-apex-card border border-apex-line p-5 grid md:grid-cols-5 gap-3 mb-6">
        <input required value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="KÓD" className="px-3 py-2 rounded-lg bg-apex-bg border border-apex-line text-apex-cream" data-testid="promo-code-input" />
        <input required type="number" min="1" max="100" value={discount} onChange={(e) => setDiscount(e.target.value)} placeholder="%" className="px-3 py-2 rounded-lg bg-apex-bg border border-apex-line text-apex-cream" data-testid="promo-discount-input" />
        <select value={type} onChange={(e) => setType(e.target.value)} className="px-3 py-2 rounded-lg bg-apex-bg border border-apex-line text-apex-cream">
          <option value="single">Jednorázový</option>
          <option value="multiple">Vícenásobný</option>
        </select>
        <ApexDatePicker
          value={expiresAt ? new Date(`${expiresAt}T00:00:00`) : undefined}
          onSelect={(d) => setExpiresAt(toIsoDate(d))}
          fromDate={new Date()}
          placeholder="Platnost do"
          testId="promo-expires-date"
        />
        {type === "multiple" ? (
          <input type="number" min="1" value={maxUses} onChange={(e) => setMaxUses(e.target.value)} placeholder="Limit" className="px-3 py-2 rounded-lg bg-apex-bg border border-apex-line text-apex-cream" />
        ) : <div />}
        <button disabled={busy} type="submit" className="btn-gold md:col-span-5 !justify-center" data-testid="promo-create-btn">Vytvořit promo kód</button>
      </form>
      <div className="space-y-2">
        {list.map(p => (
          <div key={p.id} className="rounded-xl bg-apex-card border border-apex-line p-4 flex flex-wrap items-center gap-3 justify-between">
            <div>
              <div className="text-apex-gold font-mono font-bold text-lg">{p.code}</div>
              <div className="text-xs text-apex-cream/60">{p.discount}% · {p.type} · platnost do {new Date(p.expiresAt).toLocaleDateString("cs-CZ")} · využito {p.usedCount}{p.maxUses ? `/${p.maxUses}` : ""}</div>
            </div>
            <button onClick={() => del(p.id)} className="text-xs text-red-300 hover:underline"><Trash2 className="w-3 h-3 inline" /> Smazat</button>
          </div>
        ))}
        {list.length === 0 && <div className="text-apex-cream/65">Zatím nejsou žádné promo kódy.</div>}
      </div>
    </div>
  );
}
